import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { money, fmtDate } from "@/lib/ui";
import { toast } from "sonner";
import {
  Trophy, Target, Flame, TrendingUp, Crown, ChevronRight, Loader2,
  Sparkles, Send, Bot, MapPin, DollarSign, Award, Mic, Square, Trash2, ThumbsUp, ThumbsDown,
} from "lucide-react";

const COACH_SESSION = "coach";
const SUGGESTIONS = [
  "What's my fastest path to year-end #1?",
  "Am I on track to make the CFR?",
  "How should I plan my remaining rodeos?",
  "How many wins do I need to catch the leader?",
];

// Tiny markdown renderer for the coach's replies (headings, bold, bullets, tables).
function Rich({ text, m }) {
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const lines = (text || "").split("\n");
  const out = [];
  let bullets = [];
  const flush = (key) => {
    if (bullets.length) {
      out.push(<ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-1.5">{bullets}</ul>);
      bullets = [];
    }
  };
  const inline = (s) =>
    (s || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**")
        ? <strong key={i} className={title}>{part.slice(2, -2)}</strong>
        : <span key={i}>{part}</span>);
  const cells = (l) => l.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
  const isRow = (l) => l.trim().startsWith("|") && l.includes("|");
  const isSep = (l) => /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(l) && l.includes("-");

  const border = m ? "border-white/15" : "border-[#E4E7EB]";
  const headBg = m ? "bg-white/10" : "bg-[#F1F3F5]";
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();
    // Markdown table: a row line followed by a separator line
    if (isRow(raw) && i + 1 < lines.length && isSep(lines[i + 1])) {
      flush(i);
      const header = cells(raw);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && isRow(lines[j])) { rows.push(cells(lines[j])); j += 1; }
      out.push(
        <div key={`tbl-${i}`} className={`my-2 overflow-x-auto rounded-lg border ${border}`}>
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className={headBg}>
                {header.map((h, c) => (
                  <th key={c} className={`px-2.5 py-1.5 font-display font-bold whitespace-nowrap ${title} ${c ? `border-l ${border}` : ""}`}>{inline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className={`border-t ${border}`}>
                  {header.map((_, c) => (
                    <td key={c} className={`px-2.5 py-1.5 align-top ${c ? `border-l ${border}` : ""}`}>{inline(r[c] || "")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j;
      continue;
    }
    if (!line) { flush(i); i += 1; continue; }
    if (line.startsWith("### ")) { flush(i); out.push(<div key={i} className={`font-display font-bold text-sm mt-2 ${title}`}>{inline(line.slice(4))}</div>); }
    else if (line.startsWith("## ")) { flush(i); out.push(<div key={i} className={`font-display font-bold text-base mt-2.5 ${title}`}>{inline(line.slice(3))}</div>); }
    else if (line.startsWith("# ")) { flush(i); out.push(<div key={i} className={`font-display font-extrabold text-lg mt-2.5 ${title}`}>{inline(line.slice(2))}</div>); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { bullets.push(<li key={i} className="text-sm leading-relaxed">{inline(line.slice(2))}</li>); }
    else { flush(i); out.push(<p key={i} className="text-sm leading-relaxed">{inline(line)}</p>); }
    i += 1;
  }
  flush("end");
  return <div className="space-y-1">{out}</div>;
}

function ProgressBar({ pct, color, track }) {
  return (
    <div className={`h-2 rounded-full overflow-hidden ${track}`}>
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(3, Math.min(100, pct))}%`, background: color }} />
    </div>
  );
}

export default function SeasonCommand({ m }) {
  const nav = useNavigate();
  const [plan, setPlan] = useState(undefined);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [rated, setRated] = useState({});
  const [noteIdx, setNoteIdx] = useState(null);
  const [noteText, setNoteText] = useState("");
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const autoStopRef = useRef(null);

  useEffect(() => { api.get("/me/season-plan").then((r) => setPlan(r.data)).catch(() => setPlan(null)); }, []);
  useEffect(() => {
    api.get(`/me/strategy-chat/${COACH_SESSION}`).then((r) => setMsgs(r.data.messages || [])).catch(() => {});
  }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs, sending]);

  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const sub = m ? "bg-black/20" : "bg-[#FAFBFC]";

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setMsgs((prev) => [...prev, { role: "user", content: q }]);
    setSending(true);
    let started = false;
    try {
      const token = localStorage.getItem("rp_token");
      const resp = await fetch(`${API}/me/strategy-chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ session_id: COACH_SESSION, message: q }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        if (!chunk) continue;
        setMsgs((prev) => {
          const copy = [...prev];
          if (!started) copy.push({ role: "assistant", content: chunk });
          else copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
        started = true;
      }
      if (!started) throw new Error("empty");
    } catch (e) {
      toast.error("Coach unavailable. Try again.");
      setMsgs((prev) => (prev[prev.length - 1]?.role === "user" ? prev.slice(0, -1) : prev));
    } finally { setSending(false); }
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const ext = /mp4|m4a/.test(blob.type) ? "mp4" : /wav/.test(blob.type) ? "wav" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `rec.${ext}`);
      const { data } = await api.post("/me/voice-transcribe", fd, { timeout: 45000 });
      const text = (data.text || "").trim();
      if (text) setInput((prev) => (prev ? prev + " " : "") + text);
      else toast.info("Didn't catch that — try again");
    } catch (e) {
      toast.error(e.code === "ECONNABORTED" ? "Transcription timed out — try a shorter clip" : "Couldn't transcribe. Try again.");
    } finally { setTranscribing(false); }
  };

  const startRec = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Voice isn't supported on this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mime = types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        clearTimeout(autoStopRef.current);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        if (blob.size) await transcribe(blob);
        else toast.info("No audio captured — try again");
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      autoStopRef.current = setTimeout(() => stopRec(), 60000); // cap clips at 60s
    } catch (e) {
      toast.error("Microphone access denied. Enable it to use voice.");
    }
  };

  const stopRec = () => {
    clearTimeout(autoStopRef.current);
    try { mediaRecorderRef.current?.stop(); } catch (e) { /* noop */ }
    setRecording(false);
  };

  const toggleMic = () => (recording ? stopRec() : startRec());

  const clearChat = async () => {
    setMsgs([]);
    setRated({});
    try { await api.delete(`/me/strategy-chat/${COACH_SESSION}`); } catch (e) { /* noop */ }
  };

  const sendFeedback = async (i, rating, note = "") => {
    const question = i > 0 && msgs[i - 1]?.role === "user" ? msgs[i - 1].content : "";
    const answer = msgs[i]?.content || "";
    try {
      await api.post("/me/coach-feedback", { rating, note, question, answer, session_id: COACH_SESSION });
      setRated((prev) => ({ ...prev, [i]: rating }));
      setNoteIdx(null); setNoteText("");
      toast.success(rating === "up" ? "Thanks for the feedback!" : "Thanks — we'll use this to improve the coach");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send feedback");
    }
  };

  if (plan === undefined) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  if (!plan || !plan.competitor_name) return null;

  const events = plan.events || [];
  const topEvent = events[0];
  const cfrIn = events.filter((e) => e.in_top12).length;
  const totalRemaining = (plan.remaining || []).reduce((s, r) => s + r.added_money, 0);

  return (
    <div className="space-y-5" data-testid="season-command">
      {/* Hero goal tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className={`rounded-2xl border p-4 relative overflow-hidden ${card}`} data-testid="goal-year-title">
          <div className="absolute -right-4 -top-4 opacity-10"><Crown className="w-24 h-24 text-[#E68A2E]" /></div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Year-End Race</div>
          <div className={`font-display text-3xl font-extrabold ${topEvent?.rank === 1 ? "text-[#E68A2E]" : title}`}>
            {topEvent ? `#${topEvent.rank}` : "—"}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {topEvent ? (topEvent.gap_to_leader === 0 ? "Leading the standings 🏆" : `${money(topEvent.gap_to_leader)} behind the leader`) : "No standings yet"}
          </div>
        </div>
        <div className={`rounded-2xl border p-4 relative overflow-hidden ${card}`} data-testid="goal-cfr">
          <div className="absolute -right-4 -top-4 opacity-10"><Award className="w-24 h-24 text-[#3B6B1E]" /></div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">CFR Qualification</div>
          <div className={`font-display text-3xl font-extrabold ${cfrIn > 0 ? "text-[#3B6B1E]" : title}`}>{cfrIn}/{events.length || 0}</div>
          <div className="text-xs text-slate-400 mt-0.5">{cfrIn > 0 ? `In the top 12 in ${cfrIn} event${cfrIn > 1 ? "s" : ""}` : "Chasing a top-12 spot"}</div>
        </div>
        <div className={`rounded-2xl border p-4 relative overflow-hidden ${card}`} data-testid="goal-earnings">
          <div className="absolute -right-4 -top-4 opacity-10"><DollarSign className="w-24 h-24 text-[#C8000F]" /></div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Season Earnings</div>
          <div className={`font-display text-3xl font-extrabold ${title}`}>{money(plan.total_earnings || 0)}</div>
          <div className="text-xs text-slate-400 mt-0.5">Official CPRA standings</div>
        </div>
      </div>

      {/* Per-event breakdown */}
      {events.length > 0 && (
        <div className="space-y-3">
          {events.map((e) => {
            const leadPct = e.leader_earnings > 0 ? (e.earnings / e.leader_earnings) * 100 : 100;
            const cfrPct = e.in_top12 ? 100 : (e.cfr_cutoff > 0 ? (e.earnings / e.cfr_cutoff) * 100 : 0);
            return (
              <div key={e.event} className={`rounded-2xl border p-4 ${card}`} data-testid={`event-strategy-${e.event.replace(/\s/g, "-")}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`font-display font-bold ${title}`}>{e.event}</span>
                    {e.rank === 1 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E68A2E]/15 text-[#E68A2E]">LEADER</span>}
                    {e.in_top12 && e.rank !== 1 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#3B6B1E]/15 text-[#3B6B1E]">CFR</span>}
                  </div>
                  <div className="text-right">
                    <div className={`font-display font-extrabold text-lg ${e.rank === 1 ? "text-[#E68A2E]" : title}`}>#{e.rank}</div>
                    <div className="text-[10px] text-slate-400 -mt-0.5">of {e.field_size}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="flex items-center gap-1.5 text-slate-400"><Crown className="w-3.5 h-3.5 text-[#E68A2E]" /> Gap to #1</span>
                      <span className={title}>{e.gap_to_leader === 0 ? "You lead" : `${money(e.gap_to_leader)} to ${e.leader_name}`}</span>
                    </div>
                    <ProgressBar pct={leadPct} color="#E68A2E" track={m ? "bg-white/10" : "bg-[#EEF0F2]"} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="flex items-center gap-1.5 text-slate-400"><Award className="w-3.5 h-3.5 text-[#3B6B1E]" /> CFR top-12 cutoff</span>
                      <span className={title}>
                        {e.in_top12
                          ? (e.cushion != null ? `${money(e.cushion)} cushion` : "Qualified")
                          : `${money(e.gap_to_top12)} to go`}
                      </span>
                    </div>
                    <ProgressBar pct={cfrPct} color="#3B6B1E" track={m ? "bg-white/10" : "bg-[#EEF0F2]"} />
                    <div className="text-[10px] text-slate-400 mt-1">{money(e.earnings)} won · cutoff {money(e.cfr_cutoff)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Money on the table + upcoming */}
      {(plan.remaining?.length > 0 || plan.upcoming?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {plan.remaining?.length > 0 && (
            <div className={`rounded-2xl border p-4 ${card}`} data-testid="money-on-table">
              <div className={`flex items-center gap-2 font-display font-bold mb-2 ${title}`}><Flame className="w-4 h-4 text-[#C8000F]" /> Money still on the table</div>
              <div className={`font-display text-2xl font-extrabold ${title} mb-2`}>{money(totalRemaining)}</div>
              <div className="space-y-1">
                {plan.remaining.map((r) => (
                  <div key={r.event} className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">{r.event} · {r.rodeos} rodeo{r.rodeos > 1 ? "s" : ""}</span>
                    <span className={title}>{money(r.added_money)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {plan.upcoming?.length > 0 && (
            <div className={`rounded-2xl border p-4 ${card}`} data-testid="upcoming-entries">
              <div className={`flex items-center gap-2 font-display font-bold mb-2 ${title}`}><MapPin className="w-4 h-4 text-[#E68A2E]" /> You're drawn into</div>
              <div className="space-y-1.5">
                {plan.upcoming.slice(0, 6).map((u, i) => (
                  <button key={i} onClick={() => nav(`/rodeo/${u.rodeo_id}`)} data-testid={`upcoming-${i}`}
                    className={`w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left ${m ? "hover:bg-white/5" : "hover:bg-[#FAFBFC]"}`}>
                    <span className="min-w-0">
                      <span className={`text-sm font-medium ${title}`}>{u.rodeo_name}</span>
                      <span className="block text-[11px] text-slate-400">{u.event} · {u.date ? fmtDate(u.date) : ""} · {money(u.added_money)} added</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Strategy Coach */}
      <div className={`rounded-2xl border overflow-hidden ${card}`} data-testid="strategy-coach">
        <div className={`px-4 py-3 flex items-center gap-2 border-b ${m ? "border-white/10" : "border-[#EEF0F2]"}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C8000F] to-[#E68A2E] flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
          <div className="flex-1">
            <div className={`font-display font-bold leading-tight ${title}`}>AI Strategy Coach</div>
            <div className="text-[11px] text-slate-400">Ask how to reach #1 or lock up the CFR</div>
          </div>
          {msgs.length > 0 && (
            <button onClick={clearChat} data-testid="coach-clear"
              className={`flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 ${m ? "text-slate-300 hover:bg-white/10" : "text-slate-500 hover:bg-[#F1F3F5]"}`}>
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        <div ref={scrollRef} className="max-h-[380px] overflow-y-auto px-4 py-3 space-y-3">
          {msgs.length === 0 && !sending && (
            <div className="py-4 text-center">
              <Bot className="w-8 h-8 mx-auto mb-2 text-slate-400 opacity-70" />
              <div className="text-sm text-slate-400">Your personal coach knows your rank, earnings, gaps and upcoming rodeos. Ask anything about your season plan.</div>
            </div>
          )}
          {msgs.map((mm, i) => (
            mm.role === "user" ? (
              <div key={i} className="flex justify-end" data-testid={`coach-msg-${i}`}>
                <div className="max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2.5 bg-[#C8000F] text-white">
                  <span className="text-sm">{mm.content}</span>
                </div>
              </div>
            ) : (
              <div key={i} className="flex flex-col items-start gap-1.5" data-testid={`coach-msg-${i}`}>
                <div className={`max-w-[85%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 ${m ? "bg-black/30 text-slate-200" : "bg-[#F1F3F5] text-[#14181F]"}`}>
                  <Rich text={mm.content} m={m} />
                </div>
                {mm.content && !(sending && i === msgs.length - 1) && (
                  rated[i] ? (
                    <span className="text-[11px] text-slate-400 pl-1">{rated[i] === "up" ? "✓ Thanks for the feedback" : "✓ Noted — we'll make the coach better"}</span>
                  ) : (
                    <div className="pl-1 w-full max-w-[85%]">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-400">Was this helpful?</span>
                        <button data-testid={`coach-thumbs-up-${i}`} onClick={() => sendFeedback(i, "up")} title="Good answer"
                          className={`w-7 h-7 rounded-full flex items-center justify-center ${m ? "hover:bg-white/10 text-slate-300" : "hover:bg-[#E4E7EB] text-slate-500"}`}>
                          <ThumbsUp className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`coach-thumbs-down-${i}`} onClick={() => { setNoteIdx(noteIdx === i ? null : i); setNoteText(""); }} title="Needs work"
                          className={`w-7 h-7 rounded-full flex items-center justify-center ${noteIdx === i ? "bg-[#C8000F]/15 text-[#C8000F]" : (m ? "hover:bg-white/10 text-slate-300" : "hover:bg-[#E4E7EB] text-slate-500")}`}>
                          <ThumbsDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {noteIdx === i && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <input value={noteText} onChange={(e) => setNoteText(e.target.value)} autoFocus
                            onKeyDown={(e) => e.key === "Enter" && noteText.trim() && sendFeedback(i, "down", noteText)}
                            placeholder="What was wrong or made up? Help us fix it…" data-testid={`coach-feedback-note-${i}`}
                            className={`flex-1 rounded-lg border px-3 py-2 text-xs focus:outline-none focus:border-[#C8000F] ${m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400"}`} />
                          <button data-testid={`coach-feedback-submit-${i}`} disabled={!noteText.trim()} onClick={() => sendFeedback(i, "down", noteText)}
                            className="rounded-lg bg-[#C8000F] hover:bg-[#9E0008] text-white text-xs font-semibold px-3 py-2 disabled:opacity-50">Send</button>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            )
          ))}
          {sending && (msgs.length === 0 || msgs[msgs.length - 1].role === "user") && (
            <div className="flex justify-start" data-testid="coach-thinking">
              <div className={`rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2 ${m ? "bg-black/30 text-slate-300" : "bg-[#F1F3F5] text-slate-600"}`}>
                <Loader2 className="w-4 h-4 animate-spin text-[#C8000F]" /> <span className="text-sm">Coach is working out your play…</span>
              </div>
            </div>
          )}
        </div>

        {msgs.length === 0 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} disabled={sending} data-testid={`coach-suggestion`}
                className={`text-[11px] font-medium px-2.5 py-1.5 rounded-full border ${m ? "border-white/10 text-slate-300 hover:bg-white/5" : "border-[#D7DBE0] text-slate-600 hover:bg-[#FAFBFC]"} disabled:opacity-50`}>
                <TrendingUp className="w-3 h-3 inline mr-1 text-[#3B6B1E]" />{s}
              </button>
            ))}
          </div>
        )}

        <div className={`p-3 border-t flex items-center gap-2 ${m ? "border-white/10" : "border-[#EEF0F2]"}`}>
          <button onClick={toggleMic} disabled={sending || transcribing} data-testid="coach-mic"
            title={recording ? "Stop recording" : "Speak your question"}
            className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${recording ? "bg-[#C8000F] text-white animate-pulse" : (m ? "bg-white/10 text-slate-200 hover:bg-white/20" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]")}`}>
            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Ask your strategy coach…"} data-testid="coach-input" disabled={sending}
            className={`flex-1 rounded-full border px-4 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400"}`} />
          <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="coach-send"
            className="w-10 h-10 shrink-0 rounded-full bg-[#C8000F] hover:bg-[#9E0008] text-white flex items-center justify-center disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
